import type { ConversationParticipantEntity } from '@provision/conversation-types';
import { createContext, useContextSelector } from 'use-context-selector';
import type { SmsChannelInfo } from './sms-recipients';
import type { SmsSessionGroup } from './sms-session-grouping';
import type { SmsSessionRecipientInput } from './sms-session.operations';

/** a channel the start just created, so the first send can name it before the participations cache refreshes */
export interface StartedSmsParticipation {
  conversationChannelId: string;
  externalIdentifier: string;
  /** the partner line the channel row carries — the first send must submit it verbatim */
  internalIdentifier: string;
  participantEntity: ConversationParticipantEntity;
  participantIdentity: string;
}

export interface SmsSessionRecipients {
  recipientChannelIds: string[];
  partnerPhoneNumber: string | null;
}

export interface SmsSessionContextValue {
  openSessionId: string | null;
  openedAt: string | null;
  sessionGroups: SmsSessionGroup[];
  sessionsById: ReadonlyMap<string, SmsSessionRecipients>;
  /** the conversation's TwilioSMS channels and the people behind them, oldest first */
  smsChannelsById: ReadonlyMap<string, SmsChannelInfo>;
  isLoadingChannels: boolean;
  isLoading: boolean;
  startSession: (recipients: SmsSessionRecipientInput[]) => Promise<StartedSmsParticipation[]>;
  endSession: () => Promise<void>;
  isEnding: boolean;
  showSmsSwitch: boolean;
  hasOpenSession: boolean;
  /** Non-null when the client's number is known non-sendable — disables the SMS tab. */
  smsDisabledReason: string | null;
  sendersByChannelId: ReadonlyMap<string, string>;
}

export const SmsSessionContext = createContext<SmsSessionContextValue>({
  openSessionId: null,
  openedAt: null,
  sessionGroups: [],
  sessionsById: new Map(),
  smsChannelsById: new Map(),
  isLoadingChannels: false,
  isLoading: false,
  startSession: () => Promise.resolve([]),
  endSession: () => Promise.resolve(),
  isEnding: false,
  showSmsSwitch: false,
  hasOpenSession: false,
  smsDisabledReason: null,
  sendersByChannelId: new Map(),
});

export function useSmsSessionContext<T>(selector: (v: SmsSessionContextValue) => T): T {
  return useContextSelector(SmsSessionContext, selector);
}
